import { isValidPhoneNumber } from 'libphonenumber-js';
import type { Moment } from 'moment';
import moment from 'moment';
import { getI18n } from 'react-i18next';
import isURL from 'validator/es/lib/isURL';

import { isEmailAddress } from './isEmailAddress';
import type { Validator, Values } from './types';

export type AnyDate = string | Date | Moment;

const isNil = <T>(value: undefined | null | T): value is undefined | null =>
  value === undefined || value === null;

const isEmpty = (value: unknown): boolean => typeof value === 'string' && value.trim() === '';

const match =
  <T = unknown>(testFn: (value: T, values: object) => boolean, message = ''): Validator<T> =>
  ({ value, values }) =>
    !testFn(value, values) && message;

const notAfter =
  <V = Values>(
    getTargetDate: (values: V) => AnyDate | null,
    message: string,
  ): Validator<AnyDate | null, V> =>
  ({ value, values }) => {
    const targetDate = getTargetDate(values);

    if (!value || !targetDate || moment(value).isSameOrBefore(targetDate, 'minutes')) {
      return false;
    }

    return message;
  };

const notBefore =
  <V = Values>(
    getTargetDate: (values: V) => AnyDate | null,
    message: string,
  ): Validator<AnyDate | null, V> =>
  ({ value, values }) => {
    const targetDate = getTargetDate(values);

    if (!value || !targetDate || moment(value).isSameOrAfter(targetDate, 'minutes')) {
      return false;
    }

    return message;
  };

const required =
  (message?: string): Validator =>
  ({ value }) => {
    if (Array.isArray(value) && value.length === 0) {
      return message ?? getI18n().t('validation:required');
    }

    if (isNil(value) || isEmpty(value)) {
      return message ?? getI18n().t('validation:required');
    }

    return false;
  };

const minLength =
  (min: number, message?: string): Validator<string | unknown[] | null> =>
  ({ value }) =>
    Boolean(value) &&
    value !== null &&
    (typeof value === 'string' ? value.trim() : value).length < min &&
    (message || getI18n().t('validation:minLength', { min }));

const maxLength =
  (max: number): Validator<string | unknown[] | null> =>
  ({ value }) =>
    Boolean(value) &&
    value !== null &&
    (typeof value === 'string' ? value.trim() : value).length > max &&
    getI18n().t('validation:maxLength', { max });

export interface MinMaxOptions {
  treatFloatAsPercentage?: boolean;
}

const min =
  <V = Values>(
    minValue: number | ((values: V) => number | null),
    { treatFloatAsPercentage = false }: MinMaxOptions = {},
  ): Validator<string | number | null, V> =>
  ({ value, values }) => {
    const localMinValue = typeof minValue === 'number' ? minValue : minValue(values);

    if (isNil(localMinValue)) {
      return false;
    }

    const valueAsNumber = parseFloat(String(value));
    const transformedValue = treatFloatAsPercentage ? valueAsNumber * 100 : valueAsNumber;

    return (
      transformedValue < localMinValue && getI18n().t('validation:min', { minValue: localMinValue })
    );
  };

const max =
  (
    maxValue: number,
    { treatFloatAsPercentage = false }: MinMaxOptions = {},
  ): Validator<string | number | null> =>
  ({ value }) => {
    if (isNil(value)) {
      return false;
    }

    const valueAsNumber = parseFloat(String(value));
    const transformedValue = treatFloatAsPercentage ? valueAsNumber * 100 : valueAsNumber;

    return transformedValue > maxValue && getI18n().t('validation:max', { maxValue });
  };

const notEmptyArray =
  (): Validator<unknown[]> =>
  ({ value }) =>
    (!Array.isArray(value) || value.length === 0) && getI18n().t('validation:notEmptyArray');

const email =
  (): Validator<string> =>
  ({ value }) =>
    Boolean(value) && !isEmailAddress(value) && getI18n().t('validation:email');

const mobilePhone =
  (): Validator<string> =>
  ({ value }) =>
    !isValidPhoneNumber(value) && getI18n().t('validation:mobilePhone');

const url =
  (): Validator<string> =>
  ({ value }) =>
    Boolean(value) &&
    !isURL(value, { protocols: ['http', 'https'], allow_underscores: true, disallow_auth: true }) &&
    getI18n().t('validation:url');

const notSurroundedByWhitespace =
  (): Validator<string> =>
  ({ value }) =>
    Boolean(value) && /^\s|\s$/.test(value) && getI18n().t('validation:notSurroundedByWhitespace');

const nestedValueUnique =
  <V = Values, AllV = Values>(
    getObjects: (allValues: AllV) => V[],
    getNestedValue: (values: V) => V[keyof V],
    message: string,
  ): Validator<V[keyof V], V, AllV> =>
  ({ value, allValues }) => {
    const sameNestedFields = getObjects(allValues).filter(
      (object) => getNestedValue(object) === value,
    );

    if (value && sameNestedFields.length > 1) {
      return message;
    }

    return false;
  };

const integer =
  (): Validator<string | number | null> =>
  ({ value }) =>
    Boolean(value) && parseFloat(String(value)) % 1 !== 0 && getI18n().t('validation:integer');

const iban =
  (): Validator<string> =>
  ({ value }) =>
    Boolean(value) &&
    !/^[A-Z]{2}[0-9]{2}(?: ?[0-9A-Z]{4})+ ?[0-9A-Z]{1,4}$/.test(value) &&
    getI18n().t('validation:iban');

const bic =
  (): Validator<string> =>
  ({ value }) =>
    Boolean(value) &&
    !/^[A-Z]{6}[0-9A-Z]{2}([0-9A-Z]{3})?$/i.test(value) &&
    getI18n().t('validation:bic');

const unique =
  (existingValuesArr: unknown[], message: string): Validator<unknown> =>
  ({ value }) =>
    existingValuesArr.includes(value) ? message : false;

const some =
  <T>(testFn: (value: T, values: object) => boolean, message = ''): Validator<T[]> =>
  ({ value, values }) =>
    !value.some((element) => testFn(element, values)) && message;

const hexColor =
  (): Validator<string> =>
  ({ value }) =>
    Boolean(value) &&
    !/^#(?:[0-9a-fA-F]{3}){1,2}$/i.test(value) &&
    getI18n().t('validation:hexColor');

export const is = {
  match,
  required,
  minLength,
  maxLength,
  min,
  max,
  notEmptyArray,
  email,
  mobilePhone,
  url,
  notAfter,
  notBefore,
  notSurroundedByWhitespace,
  nestedValueUnique,
  integer,
  iban,
  bic,
  unique,
  some,
  hexColor,
};
